import { useEffect, useCallback, useState, useRef } from "react";
import {
  Box,
  Card,
  CardActions,
  CardMedia,
  Grid,
  IconButton,
  Stack,
  Typography
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ShareIcon from "@mui/icons-material/Share";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import { GET_MONTAGES, DELETE_MONTAGES } from "../../constant/Apipath";
import { DeleteRequestCall } from "../../request/Deletrequest";
import { Getrequestcall } from "../../request/Getrequest";
import DeleteConfirmation from "../common/DeleteConfirmation";
import { getDbValue } from "../common/DbValue";
import Emptydata from "../common/Emptydata";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import Pageheader from "../common/Pageheader";
import { AddButton } from "../common/AddButton";
import { English } from "../../constant/EnglishFallback";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useMediaQuery, useTheme } from "@mui/material";
import Loader from "../../common/Loader";
import { setSelectedMontage } from "../../redux/actions/actions";
import ShareMontageModal from "./models/ShareMontageModal";
import PreviewMontageModal from "./models/PreviewMontageModal";
import ShareMontages from "./models/ShareMontages";
import Removeduplicates from "../common/Removeduplicate";
import AdContainer from "../common/AdContainer";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";

const Montages = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Redux selectors (only for data that needs to persist across components)
  const loginData = useSelector((state) => state.main?.loginData);
  const userID = useSelector((state) => state.main?.loginData?.userInformation?.UserId);
  const { multiLanguageData } = useSelector((state) => state?.main);
  const selectedMontage = useSelector((state) => state.main?.selectedMontage);

  // Local state
  const [mode, setMode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [dbValues, setDbValues] = useState(0);
  const [montages, setMontages] = useState([]);
  const [selectedMontagesId, setSelectedMontagesId] = useState(0);
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);

  // Computed values
  const removeDuplicates = Removeduplicates;

  const LanguageData = multiLanguageData?.Montages || {};
  const {
    TEXT_APP_HEADER = English.Montages.TEXT_APP_HEADER,
    TEXT_ADD_MONTAGE = English.Montages.TEXT_ADD_MONTAGE,
    TEXT_NO_MONTAGES = English.Montages.TEXT_NO_MONTAGES,
    TEXT_NO_IMAGE_AVAILABLE = English.Montages.TEXT_NO_IMAGE_AVAILABLE,
    TEXT_MODAL_DELETE_TITLE = English.Montages.TEXT_MODAL_DELETE_TITLE,
    TEXT_MODAL_DELETE_DESCRIPTION = English.Montages.TEXT_MODAL_DELETE_DESCRIPTION,
    BUTTON_ADD_SOUND = English.Montages.BUTTON_ADD_SOUND
  } = LanguageData || {};

  useEffect(() => {
    fetchMontages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectMontage = useCallback(
    (newMontage) => {
      dispatch(setSelectedMontage(newMontage));
    },
    [dispatch]
  );

  const fetchMontages = useCallback(async () => {
    setLoading(true);
    try {
      const response = await Getrequestcall(`${GET_MONTAGES}/${userID}`, loginData?.accessToken);
      const sortArr = response.data?.data?.sort((a, b) => b.id - a.id) || [];
      setMontages(sortArr);
    } catch (error) {
      toast.error("Failed to load montages. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [userID, loginData]);

  const onTradeItemClick = useCallback((id) => {
    if (!id) return;
    setSelectedMontagesId(id);
    setConfirmDeleteModalOpen(true);
  }, []);

  const handleDeleteMontage = useCallback(async () => {
    try {
      const response = await DeleteRequestCall(
        `${DELETE_MONTAGES}${selectedMontagesId}/${userID}`,
        loginData?.accessToken
      );
      if (response.status === 200) {
        toast.success("Montage deleted successfully!");
        setConfirmDeleteModalOpen(false);
        fetchMontages();
      }
    } catch (error) {
      toast.error("Failed to delete montage. Please try again.");
    }
  }, [selectedMontagesId, userID, loginData, fetchMontages]);

  // Handle Modal Triggers
  const handleAdd = useCallback(() => {
    setMode("add");
    handleSelectMontage(null);
    navigate("/montages/add");
  }, [handleSelectMontage, navigate]);

  const handleEdit = useCallback(
    (montage) => {
      setMode("edit");
      handleSelectMontage(montage);
      navigate(`/montages/edit/${montage.id}`);
    },
    [handleSelectMontage, navigate]
  );

  const handleShare = useCallback(
    (data) => {
      const dbValue = getDbValue();
      setDbValues(dbValue);
      if (data) {
        handleSelectMontage(data);
        setShareOpen(true);
      }
    },
    [handleSelectMontage]
  );

  const handlePreview = useCallback(
    (data) => {
      handleSelectMontage(data);
      setPreviewModalOpen(true);
    },
    [handleSelectMontage]
  );

  return (
    <Box className="main-content-Box" sx={{ p: isSmallScreen ? 2 : 3, pt: 2 }}>
      <Pageheader headerName={TEXT_APP_HEADER} />
      <Card className="main-content-card">
        {loading ? (
          <Loader />
        ) : (
          <Box className="main-content-card-list">
            {montages?.length > 0 ? (
              <Grid container spacing={{ xs: 1.5, md: 2 }}>
                {montages.map((data) => {
                  const handleEditClick = () => handleEdit(data);
                  const handleShareClick = () => handleShare(data);
                  const handleDeleteClick = () => onTradeItemClick(data.id);
                  const handlePreviewClick = () => handlePreview(data);
                  const firstItem = data.fileList?.[0];
                  const isVideo =
                    firstItem?.fileType?.toLowerCase() === "video" ||
                    firstItem?.fileUrl?.toLowerCase().endsWith(".mp4");

                  return (
                    <Grid key={data.id} size={{ xs: 12, sm: 6, md: 6, lg: 4, xl: 3 }}>
                      <Card className="pagelist-content">
                        {data.fileList?.length > 0 ? (
                          <Box
                            position="relative"
                            onClick={handlePreviewClick}
                            sx={{ cursor: "pointer", height: 200 }}>
                            {isVideo ? (
                              <CardMedia
                                component="video"
                                height="200"
                                src={firstItem?.fileUrl}
                                sx={{ objectFit: "cover" }}
                              />
                            ) : (
                              <CardMedia
                                component="img"
                                height="200"
                                image={data.fileList[0]?.fileUrl}
                                alt={data.name}
                                sx={{ objectFit: "cover" }}
                              />
                            )}

                            {/* Play Overlay */}
                            <Box
                              position="absolute"
                              top="50%"
                              left="50%"
                              sx={{
                                transform: "translate(-50%, -50%)",
                                bgcolor: "rgba(0,0,0,0.3)",
                                borderRadius: "50%",
                                p: 1,
                                display: "flex"
                              }}>
                              <PlayArrowIcon sx={{ color: "white", fontSize: 40 }} />
                            </Box>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              height: "200px",
                              backgroundColor: "#f0f0f0",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center"
                            }}>
                            <Typography variant="body2">{TEXT_NO_IMAGE_AVAILABLE}</Typography>
                          </Box>
                        )}
                        <CardActions
                          disableSpacing
                          sx={{ flexDirection: "column", alignItems: "baseline" }}>
                          <Stack
                            direction="row"
                            spacing={0.1}
                            justifyContent={"space-between"}
                            width={"100%"}>
                            <Typography variant="body1" maxWidth={"170px"} noWrap>
                              {data.name}
                            </Typography>
                            <Stack direction="row">
                              <IconButton size="small" color="primary" onClick={handleEditClick}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" color="primary" onClick={handleShareClick}>
                                <ShareIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" color="error" onClick={handleDeleteClick}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Stack>
                        </CardActions>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Box className="empty-data-card">
                <Emptydata
                  icon={<PlayArrowIcon className="emptydata-icon" />}
                  firstText={TEXT_NO_MONTAGES}
                />
              </Box>
            )}
          </Box>
        )}
        <AddButton toolTipText={TEXT_ADD_MONTAGE} onAddClick={handleAdd} />

        <ShareMontageModal
          open={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          selectedMontage={selectedMontage}
          loginData={loginData}
          userID={userID}
          dbValues={dbValues}
        />

        <ShareMontages
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          selectedMontage={selectedMontage}
          loginData={loginData}
          dbValues={dbValues}
        />

        <PreviewMontageModal
          open={previewModalOpen}
          onClose={() => setPreviewModalOpen(false)}
          selectedMontage={selectedMontage}
        />

        {confirmDeleteModalOpen && (
          <DeleteConfirmation
            open={confirmDeleteModalOpen}
            onClose={() => setConfirmDeleteModalOpen(false)}
            headerText={TEXT_MODAL_DELETE_TITLE}
            secondaryText={TEXT_MODAL_DELETE_DESCRIPTION}
            onBadgeClick={handleDeleteMontage}
          />
        )}
        <AdContainer />
      </Card>
    </Box>
  );
};

export default Montages;
